import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardContent, PaginatedTable } from '../../components/ui';
import type { PaginationState } from '../../components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { adminBackofficeApi } from '../../services/adminBackoffice';

interface PaymentItem {
  id: string;
  academyId?: string;
  academyName?: string | null;
  amount: number;
  status: string;
  createdAt: string | Date;
}

export default function PaymentsPage() {
  const [payments, setPayments] = useState<PaymentItem[]>([]);
  const [pagination, setPagination] = useState<PaginationState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    adminBackofficeApi
      .getPayments({ page, limit: 20 })
      .then((data) => {
        if (data.success && data.data) {
          setPayments(data.data.payments || []);
          setPagination(data.data.pagination || null);
        } else setError(data.error || 'Failed to load');
      })
      .catch((err) => setError(err?.response?.data?.error || err?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [page]);

  if (error && payments.length === 0) {
    return (
      <div>
        <PageHeader
          title="Payments"
          help={<p>View all academy lesson payments across the platform.</p>}
        />
        <div className="rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20 p-4 text-red-700 dark:text-red-300">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Payments"
        help={<p>View all academy lesson payments across the platform.</p>}
      />
      <Card>
        <CardHeader>
          <h2 className="text-lg font-medium">All academy payments</h2>
        </CardHeader>
        <CardContent>
          <PaginatedTable<PaymentItem>
            columns={[
              { key: 'academy', header: 'Academy', render: (p) => p.academyName ?? p.academyId ?? '–' },
              { key: 'amount', header: 'Amount', className: 'text-right', render: (p) => (p.amount / 100).toFixed(2) },
              { key: 'status', header: 'Status', render: (p) => p.status },
              {
                key: 'date',
                header: 'Date',
                render: (p) => (p.createdAt ? new Date(p.createdAt).toLocaleString() : '–'),
              },
            ]}
            data={payments}
            pagination={pagination}
            loading={loading}
            emptyMessage="No payments found."
            onPageChange={setPage}
          />
        </CardContent>
      </Card>
    </div>
  );
}
